import { Alert, Empty, Tabs, Tag } from "@arco-design/web-react";
import { ChevronDown, ChevronUp, Eye, RotateCcw } from "lucide-react";
import { Fragment, useId, useState } from "react";

import { Button } from "../../../shared/ui/shadcn/button";
import { Pagination } from "../../../shared/ui/shadcn/pagination";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../shared/ui/shadcn/table";
import { useWorkspaceFeeAdjustmentRecords } from "../../fee-adjustment/runtime";
import type { FeeAdjustmentPricing } from "../../fee-adjustment/types";
import { FeeAdjustmentRecordDetailModal } from "../../fee-adjustment/ui/FeeAdjustmentRecordDetail";
import { feeAdjustmentSpecUnit } from "../service";
import type { FulfillmentOrderChangeBatch } from "../types";
import "./fulfillmentOrderFeeHistory.css";

type Props = {
  workspaceId: string;
  canAccessRequests: boolean;
  changes: FulfillmentOrderChangeBatch[];
  changesLoading?: boolean;
  changesError?: unknown;
  onRetryChanges: () => void;
  collapsible?: boolean;
};

const numberText = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2, useGrouping: false }).format(number) : "-";
};
const money = (value: unknown) => numberText(value) === "-" ? "-" : `¥${numberText(value)}`;
const dateTime = (value: string) => new Date(value).toLocaleString("zh-CN", { hour12: false });
const snapshot = (value: unknown): Record<string, unknown> => (
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {}
);
const snapshotName = (value: Record<string, unknown>) => {
  const name = value.item_name_snapshot ?? value.itemNameSnapshot;
  return typeof name === "string" && name.trim() ? name : "未命名项目";
};
const changeTypeLabels: Record<NonNullable<FulfillmentOrderChangeBatch["changeType"]>, string> = {
  amount_adjustment: "金额增减", structured_adjustment: "数量规格增减", main_item_added: "新增项目",
  main_item_changed: "项目变更", main_item_deleted: "删除项目", legacy_change: "确认件变更",
};

/** 使用账本差额决定颜色，展示格式不参与金额计算。 */
function DeltaAmount({ value }: { value: string }) {
  const direction = Number(value) > 0 ? "increase" : Number(value) < 0 ? "decrease" : "same";
  return <span className="fulfillment-fee-history-money" data-direction={direction}>{money(value)}</span>;
}

/** 计价快照保持数量、规格、单价各自的单位。 */
function pricingText(value: FeeAdjustmentPricing | Record<string, unknown> | null) {
  if (!value) return "-";
  const resourceType = "resource_type" in value && typeof value.resource_type === "string" ? value.resource_type : "";
  const specUnit = value.spec_unit ?? (resourceType ? feeAdjustmentSpecUnit(resourceType) : "");
  return `${numberText(value.quantity)} ${value.unit ?? ""} × ${numberText(value.billing_count)} ${specUnit} × ${money(value.unit_price_amount)}`;
}

/** 原确认件账本按明细快照回显，批次金额与单行金额分别展示。 */
function DirectDetails({ batch }: { batch: FulfillmentOrderChangeBatch }) {
  return <div className="fulfillment-fee-history-details">
    <Table aria-label="计调直接增减项目明细" density="compact">
      <TableHead><TableRow>{["项目", "操作", "原数量 / 规格 / 单价", "调整后数量 / 规格 / 单价", "账本明细金额"].map((title) => <TableHeader key={title}>{title}</TableHeader>)}</TableRow></TableHead>
      <TableBody>{batch.items.map((item) => {
        const structured = batch.changeType === "structured_adjustment";
        const before = structured ? snapshot(item.snapshot.before) : item.action === "add" ? null : item.snapshot;
        const after = structured ? snapshot(item.snapshot.after) : item.action === "add" ? item.snapshot : null;
        return <TableRow key={item.id}>
          <TableCell>{snapshotName(after ?? before ?? item.snapshot)}</TableCell>
          <TableCell>{item.action === "adjust" ? "增减" : item.action === "add" ? "新增" : "删除"}</TableCell>
          <TableCell>{pricingText(before)}</TableCell><TableCell>{pricingText(after)}</TableCell>
          <TableCell>{item.action === "adjust" ? <DeltaAmount value={item.amount} /> : money(item.amount)}</TableCell>
        </TableRow>;
      })}</TableBody>
    </Table>
  </div>;
}

/** 确认件页底部的独立历史区域，两个账本分别读取、分页和展示。 */
export function FulfillmentOrderFeeHistoryPanel({ workspaceId, canAccessRequests, changes, changesLoading, changesError, onRetryChanges, collapsible = false }: Props) {
  const contentId = useId();
  const [collapsed, setCollapsed] = useState(false);
  const records = useWorkspaceFeeAdjustmentRecords(workspaceId, canAccessRequests);
  const [source, setSource] = useState(canAccessRequests ? "request" : "direct");
  const [pages, setPages] = useState({ request: { page: 1, pageSize: 10 }, direct: { page: 1, pageSize: 10 } });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<{ workspaceId: string; requestId: string } | null>(null);
  const isRequest = canAccessRequests && source === "request";
  const currentSource = isRequest ? "request" : "direct";
  const requestRows = [...(records.data ?? [])].filter((record) => record.status === "processed").sort((left, right) => (right.processed_at ?? right.created_at).localeCompare(left.processed_at ?? left.created_at));
  const directRows = [...changes].sort((left, right) => right.createdAt.localeCompare(left.createdAt));
  const total = isRequest ? requestRows.length : directRows.length;
  const { pageSize } = pages[currentSource];
  const page = Math.min(pages[currentSource].page, Math.max(1, Math.ceil(total / pageSize)));
  const start = (page - 1) * pageSize;
  const error = isRequest ? records.isError : Boolean(changesError);
  const loading = isRequest ? records.isLoading : changesLoading;
  const toggle = (id: string) => setExpandedId((current) => current === id ? null : id);

  return <section className="fulfillment-fee-history" aria-label="人员费用调整明细" aria-busy={Boolean(loading)}>
    <header className="fulfillment-fee-history-heading">
      <h3>{collapsible ? <button type="button" className="fulfillment-fee-history-toggle" aria-expanded={!collapsed} aria-controls={contentId} onClick={() => setCollapsed((current) => !current)}>
        {collapsed ? <ChevronDown aria-hidden="true" /> : <ChevronUp aria-hidden="true" />}人员费用调整明细
      </button> : "人员费用调整明细"}</h3>
      <Button variant="ghost" size="sm" aria-label="刷新调整明细" onClick={() => isRequest ? void records.refetch() : onRetryChanges()}><RotateCcw aria-hidden="true" />刷新</Button>
    </header>
    <div id={contentId} hidden={collapsible && collapsed}>
    <Tabs activeTab={currentSource} onChange={(key) => { setSource(key); setExpandedId(null); setSelectedRequest(null); }}>
      {canAccessRequests ? <Tabs.TabPane key="request" title={`费用调整申请（${requestRows.length}）`} /> : null}
      <Tabs.TabPane key="direct" title={`计调直接增减（${directRows.length}）`} />
    </Tabs>
    {error ? <Alert type="error" content={isRequest ? "费用调整申请记录加载失败" : "计调直接增减记录加载失败"} action={<Button variant="ghost" size="sm" onClick={() => isRequest ? void records.refetch() : onRetryChanges()}>重试</Button>} /> : null}
    {!error && loading ? <div role="status">正在加载调整明细</div> : null}
    {!error && !loading && total === 0 ? <Empty description={isRequest ? "暂无已完成的费用调整申请" : "暂无计调直接增减记录"} /> : null}
    {!error && !loading && total > 0 ? <>
      <Table aria-label={isRequest ? "费用调整申请记录" : "计调直接增减记录"} density="compact" stickyHeader containerClassName="fulfillment-fee-history-table-wrap">
        <TableHead><TableRow>{(isRequest
          ? ["来源", "处理时间", "调整需求", "成本变化", "人员应收变化", "处理依据", "操作"]
          : ["来源", "调整时间", "调整内容", "调整前总额", "调整后总额", "成本变化", "原因", "操作"]
        ).map((title) => <TableHeader key={title}>{title}</TableHeader>)}</TableRow></TableHead>
        <TableBody>{isRequest ? requestRows.slice(start, start + pageSize).map((record) => <Fragment key={record.request_id}>
          <TableRow>
            <TableCell><Tag color="arcoblue">费用调整申请</Tag></TableCell><TableCell>{dateTime(record.processed_at ?? record.created_at)}</TableCell>
            <TableCell className="fulfillment-fee-history-description">{record.request_text}</TableCell>
            <TableCell><DeltaAmount value={record.total_cost_delta_amount} /></TableCell><TableCell><DeltaAmount value={record.total_receivable_delta_amount} /></TableCell>
            <TableCell className="fulfillment-fee-history-description">{record.processing_basis || "-"}</TableCell>
            <TableCell><Button variant="ghost" size="sm" aria-label={`查看申请 ${record.request_text}`} aria-haspopup="dialog" onClick={() => setSelectedRequest({ workspaceId, requestId: record.request_id })}><Eye aria-hidden="true" />详情</Button></TableCell>
          </TableRow>
        </Fragment>) : directRows.slice(start, start + pageSize).map((batch) => <Fragment key={batch.id}>
          <TableRow>
            <TableCell><Tag color="cyan">计调直接增减</Tag></TableCell><TableCell>{dateTime(batch.createdAt)}</TableCell>
            <TableCell>{changeTypeLabels[batch.changeType ?? "legacy_change"]} · {batch.items.length} 项</TableCell>
            <TableCell>{money(batch.amountBefore)}</TableCell><TableCell>{money(batch.amountAfter)}</TableCell><TableCell><DeltaAmount value={batch.deltaAmount} /></TableCell>
            <TableCell className="fulfillment-fee-history-description">{batch.reason || "-"}</TableCell>
            <TableCell><Button variant="ghost" size="sm" aria-label={`查看直接增减 ${batch.reason || dateTime(batch.createdAt)}`} aria-expanded={expandedId === batch.id} onClick={() => toggle(batch.id)}>{expandedId === batch.id ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}{expandedId === batch.id ? "收起" : "详情"}</Button></TableCell>
          </TableRow>
          {expandedId === batch.id ? <TableRow><TableCell colSpan={8}><DirectDetails batch={batch} /></TableCell></TableRow> : null}
        </Fragment>)}</TableBody>
      </Table>
      <Pagination page={page} pageSize={pageSize} total={total} pageSizeOptions={[10, 20, 50]} onChange={(nextPage, nextPageSize) => { setPages((current) => ({ ...current, [currentSource]: { page: nextPage, pageSize: nextPageSize } })); setExpandedId(null); }} />
    </> : null}
    </div>
    <FeeAdjustmentRecordDetailModal target={canAccessRequests && selectedRequest?.workspaceId === workspaceId ? selectedRequest : null} onClose={() => setSelectedRequest(null)} />
  </section>;
}
